import {
  useEffect,
  useId,
  useRef,
  type FocusEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { MuiTelInput, type MuiTelInputProps } from 'mui-tel-input';
import Box from '../primitives/Box';
import { fieldLabelSx, outlinedFieldSx } from '../styles/formControlStyles';
import { mergeSx } from '../utils/mergeSx';
import CountryFlag from './CountryFlag';
import PhoneCountryMenuList from './PhoneCountryMenuList';

type PhoneBlurEvent = FocusEvent<HTMLInputElement | HTMLTextAreaElement>;

type MuiTelSlotProps = NonNullable<MuiTelInputProps['slotProps']>;

export type PhoneTextFieldProps = {
  value?: string;
  /**
   * Raw digits the user typed (may include `0`, `84`, `+84`, spaces).
   * Canonical E.164 is applied on save (`parsePhoneInput` / backend), not while typing.
   */
  onChange?: (value: string) => void;
  onBlur?: (event: PhoneBlurEvent) => void;
  label?: ReactNode;
  labelEnd?: ReactNode;
  placeholder?: string;
  helperText?: ReactNode;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  id?: string;
  name?: string;
  size?: 'small' | 'medium';
  inputRef?: Ref<HTMLInputElement>;
  sx?: SxProps<Theme>;
  margin?: 'none' | 'dense' | 'normal';
  /** Pass-through for a11y (`aria-label`) when the field has no visible label. */
  slotProps?: {
    htmlInput?: MuiTelSlotProps['htmlInput'];
  };
};

interface FieldLabelProps {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
  sx?: SxProps<Theme>;
}

const FieldLabelRoot = styled('label')({});

const FieldLabel = ({ children, required, htmlFor, sx }: FieldLabelProps) => (
  <FieldLabelRoot htmlFor={htmlFor} sx={mergeSx(fieldLabelSx, sx)}>
    {children}
    {required ? ' *' : null}
  </FieldLabelRoot>
);

const labeledMarginSx = (margin: PhoneTextFieldProps['margin']): SxProps<Theme> | undefined => {
  if (margin === 'normal') return { mt: 2, mb: 1 };
  if (margin === 'dense') return { mt: 1, mb: 0.5 };
  return undefined;
};

/** Library sets dial-code Typography to pointer-events:none; reopen menu from whole adornment. */
const phoneTelFieldSx: SxProps<Theme> = {
  '& .MuiInputAdornment-positionStart': {
    cursor: 'pointer',
  },
  '& .MuiInputAdornment-positionStart .MuiTypography-root': {
    pointerEvents: 'auto',
    cursor: 'pointer',
  },
};

/**
 * Phone input with country flag (default VN).
 * Form value stays as typed (`0…`, `84…`, `+84…`); validation uses `isValidPhoneInput`.
 * Persist as E.164 via `parsePhoneInput` / backend normalize on save.
 */
const PhoneTextField = ({
  value,
  onChange,
  onBlur,
  label,
  labelEnd,
  placeholder,
  helperText,
  error,
  disabled,
  required,
  fullWidth = true,
  id,
  name,
  size = 'small',
  inputRef,
  sx,
  margin,
  slotProps,
}: PhoneTextFieldProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || disabled) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const adornment = target.closest('.MuiInputAdornment-positionStart');
      if (!adornment || !root.contains(adornment)) return;
      if (target.closest('button.MuiTelInput-IconButton')) return;
      adornment.querySelector<HTMLButtonElement>('button.MuiTelInput-IconButton')?.click();
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [disabled]);

  const input = (
    <Box ref={rootRef} sx={fullWidth ? { width: '100%' } : undefined}>
      <MuiTelInput
        id={inputId}
        name={name}
        value={value ?? ''}
        onChange={(next) => onChange?.(next)}
        onBlur={(event) => onBlur?.(event)}
        inputRef={inputRef}
        defaultCountry="VN"
        preferredCountries={['VN']}
        focusOnSelectCountry
        langOfCountryName="vi"
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        margin={label == null ? margin : 'none'}
        getFlagElement={(isoCode) => <CountryFlag code={isoCode} />}
        slotProps={slotProps}
        MenuProps={{
          disableAutoFocusItem: true,
          slots: { list: PhoneCountryMenuList },
          // Re-declare list a11y: spreading MenuProps replaces mui-tel-input slotProps.
          slotProps: {
            list: {
              role: 'listbox',
              'aria-labelledby': 'select-country',
            },
            paper: {
              sx: {
                width: 320,
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: 360,
              },
            },
          },
        }}
        sx={mergeSx(outlinedFieldSx, phoneTelFieldSx, sx)}
      />
    </Box>
  );

  if (label == null) {
    return input;
  }

  return (
    <Box
      sx={mergeSx(
        {
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: fullWidth ? '100%' : undefined,
        },
        labeledMarginSx(margin),
      )}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
        }}
      >
        <FieldLabel htmlFor={inputId} required={required} sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {label}
        </FieldLabel>
        {labelEnd}
      </Box>
      {input}
    </Box>
  );
};

export default PhoneTextField;
